import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const LUGARES = [
    'BOLO SAN ISIDRO','PALMIRA','LAURO','CANDELARIA','LA REGINA',
    'SAN ANTONIO','PRADERA','SANTA ANA','CHOCOCITO','REDES SOCIALES'
];

const IA_OPCIONES = ['Claude','Gemini','ChatGPT','Canva','Midjourney','Adobe Firefly','Ideogram','Otra'];

const esImagen = (mime) => mime && mime.startsWith('image/');
const esPDF    = (mime) => mime === 'application/pdf';
const esVideo  = (mime) => mime && mime.startsWith('video/');
const esExcel  = (mime) => mime && (mime.includes('spreadsheet') || mime.includes('excel'));

const IA_VIDEO = ['Sora (OpenAI)','Runway ML','Kling AI','Pika Labs','Luma Dream Machine','Otra'];

const EMPTY_FORM = {
    nombre: '', lugares: [], usa_prompt: false,
    prompt: '', ia_usada: '', notas: '', estado: 'en_uso'
};

const Publicidad = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [search, setSearch] = useState('');
    const fileRef = useRef();
    const [tipoArchivo, setTipoArchivo] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/publicidad');
            setItems(data);
        } catch {
            setError('Error cargando publicidades');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setPreview(null);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            nombre: item.nombre,
            lugares: item.lugares || [],
            usa_prompt: !!item.usa_prompt,
            prompt: item.prompt || '',
            ia_usada: item.ia_usada || '',
            notas: item.notas || '',
            estado: item.estado
        });
        setEditingId(item.id);
        setPreview(`/uploads/media/${item.archivo}`);
        setError('');
        setModalOpen(true);
    };

    const openViewer = (item) => {
        setSelected(item);
        setViewerOpen(true);
    };

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setTipoArchivo(file.type);
        if (file.type.startsWith('image/')) {
            setPreview(URL.createObjectURL(file));
        } else {
            setPreview(null);
        }
        // PDF no necesita prompt
        if (file.type === 'application/pdf' || file.type.includes('spreadsheet') || file.type.includes('excel')) {
            setForm(p => ({ ...p, usa_prompt: false }));
        }
    };

    const toggleLugar = (lugar) => {
        setForm(prev => ({
            ...prev,
            lugares: prev.lugares.includes(lugar)
                ? prev.lugares.filter(l => l !== lugar)
                : [...prev.lugares, lugar]
        }));
    };

    const handleSubmit = async () => {
        if (!form.nombre) { setError('El nombre es requerido'); return; }
        if (form.lugares.length === 0) { setError('Seleccione al menos un lugar'); return; }
        if (!editingId && !fileRef.current?.files[0]) { setError('La imagen es requerida'); return; }

        setSaving(true); setError('');
        try {
            const fd = new FormData();
            fd.append('nombre', form.nombre);
            fd.append('lugares', JSON.stringify(form.lugares));
            fd.append('usa_prompt', form.usa_prompt ? '1' : '0');
            fd.append('prompt', form.prompt);
            fd.append('ia_usada', form.ia_usada);
            fd.append('notas', form.notas);
            fd.append('estado', form.estado);
            if (fileRef.current?.files[0]) fd.append('archivo', fileRef.current.files[0]);

            if (editingId) {
                await api.post(`/publicidad/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                showSuccess('Publicidad actualizada');
            } else {
                await api.post('/publicidad', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                showSuccess('Publicidad creada');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta publicidad?')) return;
        try {
            await api.delete(`/publicidad/${id}`);
            showSuccess('Publicidad eliminada');
            setViewerOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error eliminando');
        }
    };

    const copyPrompt = (prompt) => {
        navigator.clipboard.writeText(prompt);
    };

    const downloadImage = (item) => {
        const a = document.createElement('a');
        a.href = `/uploads/media/${item.archivo}`;
        a.download = item.nombre;
        a.click();
    };

    const filtered = items.filter(i =>
        i.nombre.toLowerCase().includes(search.toLowerCase()) ||
        (i.lugares || []).some(l => l.toLowerCase().includes(search.toLowerCase()))
    );

    const formatBytes = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    };

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Publicidad</h2>
                <button onClick={openCreate}
                    className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                    style={{ backgroundColor: 'var(--color-primary)' }}>
                    + Nueva publicidad
                </button>
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            {/* Buscador */}
            <div className="mb-4">
                <input type="text" placeholder="Buscar por nombre o lugar..." value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
            </div>

            {/* Galería */}
            {loading ? (
                <div className="text-center text-gray-400 py-12 text-sm">Cargando...</div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-gray-400 py-12 text-sm">No hay publicidades registradas</div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {filtered.map(item => (
                        <div key={item.id} onClick={() => openViewer(item)}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-shadow">
                            <div className="aspect-square bg-gray-100 overflow-hidden flex items-center justify-center">
                                {esImagen(item.mime_type) ? (
                                    <img src={`/uploads/media/${item.archivo}`} alt={item.nombre}
                                        className="w-full h-full object-cover" />
                                ) : esPDF(item.mime_type) ? (
                                    <div className="flex flex-col items-center justify-center gap-1">
                                        <span className="text-4xl">📄</span>
                                        <span className="text-xs text-red-600 font-bold">PDF</span>
                                    </div>
                                ) : esVideo(item.mime_type) ? (
                                    <div className="flex flex-col items-center justify-center gap-1">
                                        <span className="text-4xl">🎬</span>
                                        <span className="text-xs text-gray-600 font-bold">VIDEO</span>
                                    </div>
                                ) : esExcel(item.mime_type) ? (
                                    <div className="flex flex-col items-center justify-center gap-1">
                                        <span className="text-4xl">📊</span>
                                        <span className="text-xs text-green-600 font-bold">EXCEL</span>
                                    </div>
                                ) : (
                                    <span className="text-4xl">📁</span>
                                )}
                            </div>
                            <div className="p-2">
                                <p className="text-xs font-medium text-gray-800 truncate">{item.nombre}</p>
                                <div className="flex items-center justify-between mt-1">
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${item.estado === 'en_uso' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                        {item.estado === 'en_uso' ? 'En uso' : 'Sin uso'}
                                    </span>
                                    <span className="text-xs text-gray-400">{formatBytes(item.tamanio)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Viewer modal */}
            {viewerOpen && selected && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setViewerOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{selected.nombre}</h3>
                            <button onClick={() => setViewerOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>

                        {/* Visor según tipo */}
                        <div className="bg-gray-100 flex items-center justify-center p-4">
                            {esImagen(selected.mime_type) ? (
                                <img src={`/uploads/media/${selected.archivo}`} alt={selected.nombre}
                                    className="max-w-full max-h-80 object-contain rounded-lg" />
                            ) : esPDF(selected.mime_type) ? (
                                <embed src={`/uploads/media/${selected.archivo}`}
                                    type="application/pdf" width="100%" height="400px"
                                    className="rounded-lg" />
                            ) : esVideo(selected.mime_type) ? (
                                <video controls className="max-w-full max-h-80 rounded-lg">
                                    <source src={`/uploads/media/${selected.archivo}`} type={selected.mime_type} />
                                </video>
                            ) : esExcel(selected.mime_type) ? (
                                <div className="flex flex-col items-center gap-3 py-8">
                                    <span className="text-6xl">📊</span>
                                    <p className="text-sm text-gray-600">{selected.archivo}</p>
                                    <p className="text-xs text-gray-400">Descarga el archivo para verlo</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-3 py-8">
                                    <span className="text-6xl">📁</span>
                                    <p className="text-sm text-gray-600">{selected.archivo}</p>
                                </div>
                            )}
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Lugares */}
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Lugares de uso</p>
                                <div className="flex flex-wrap gap-1">
                                    {(selected.lugares || []).map(l => (
                                        <span key={l} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{l}</span>
                                    ))}
                                </div>
                            </div>

                            {/* Estado */}
                            <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${selected.estado === 'en_uso' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {selected.estado === 'en_uso' ? '✓ En uso' : '○ Sin uso'}
                                </span>
                                <span className="text-xs text-gray-400">{formatBytes(selected.tamanio)}</span>
                                <span className="text-xs text-gray-400">{new Date(selected.created_at).toLocaleDateString('es-CO')}</span>
                            </div>

                            {/* Prompt */}
                            {selected.usa_prompt && selected.prompt && (
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold text-gray-500 uppercase">Prompt usado</p>
                                        <div className="flex gap-2">
                                            {selected.ia_usada && (
                                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{selected.ia_usada}</span>
                                            )}
                                            <button onClick={() => copyPrompt(selected.prompt)}
                                                className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-0.5 rounded-full">
                                                📋 Copiar
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.prompt}</p>
                                </div>
                            )}

                            {/* Notas */}
                            {selected.notas && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Notas</p>
                                    <p className="text-sm text-gray-700">{selected.notas}</p>
                                </div>
                            )}

                            {/* Creado por */}
                            {selected.creado_por && (
                                <p className="text-xs text-gray-400">Creado por: {selected.creado_por}</p>
                            )}
                        </div>

                        {/* Acciones */}
                        <div className="px-6 pb-6 flex gap-2 flex-wrap">
                            <button onClick={() => downloadImage(selected)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                ⬇️ Descargar
                            </button>
                            <button onClick={() => { setViewerOpen(false); openEdit(selected); }}
                                className="flex-1 border border-blue-300 text-blue-600 font-semibold py-2.5 rounded-lg text-sm">
                                ✏️ Editar
                            </button>
                            <button onClick={() => handleDelete(selected.id)}
                                className="flex-1 border border-red-200 text-red-500 font-semibold py-2.5 rounded-lg text-sm">
                                🗑 Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal crear/editar */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar publicidad' : 'Nueva publicidad'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            {/* Nombre */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                                <input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="Ej: Promo Fibra Bolo Diciembre 2024" />
                            </div>

                            {/* Imagen */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Imagen {editingId ? '(dejar vacío para mantener la actual)' : '*'}
                                </label>
                                <input type="file" ref={fileRef} onChange={handleFile} accept="image/*"
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm" />
                                {preview && (
                                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center p-2">
                                        <img src={preview} alt="preview" className="max-h-32 object-contain" />
                                    </div>
                                )}
                            </div>

                            {/* Lugares */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Lugares de uso *</label>
                                <div className="flex flex-wrap gap-2">
                                    {LUGARES.map(l => (
                                        <button key={l} type="button" onClick={() => toggleLugar(l)}
                                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                                form.lugares.includes(l)
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                            }`}>
                                            {l}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Prompt — solo para imágenes y videos */}
                            {!esPDF(tipoArchivo || (editingId ? '' : '')) && !esExcel(tipoArchivo || '') && (
                            <div className="border border-gray-200 rounded-xl p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-sm font-medium text-gray-700">¿Usó prompt de IA?</span>
                                    <button type="button" onClick={() => setForm(p => ({ ...p, usa_prompt: false }))}
                                        className={`text-xs px-3 py-1 rounded-full border ${!form.usa_prompt ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-300 text-gray-500'}`}>
                                        No
                                    </button>
                                    <button type="button" onClick={() => setForm(p => ({ ...p, usa_prompt: true }))}
                                        className={`text-xs px-3 py-1 rounded-full border ${form.usa_prompt ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-500'}`}>
                                        Sí
                                    </button>
                                </div>
                                {form.usa_prompt && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">IA utilizada</label>
                                            <select value={IA_OPCIONES.includes(form.ia_usada) ? form.ia_usada : (form.ia_usada ? 'Otra' : '')}
                                                onChange={e => setForm(p => ({ ...p, ia_usada: e.target.value === 'Otra' ? '' : e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                                <option value="">Seleccionar...</option>
                                                {IA_OPCIONES.map(ia => <option key={ia}>{ia}</option>)}
                                            </select>
                                            {(!IA_OPCIONES.includes(form.ia_usada) || form.ia_usada === '') && form.ia_usada !== undefined && (
                                                <input value={form.ia_usada} onChange={e => setForm(p => ({ ...p, ia_usada: e.target.value }))}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-2"
                                                    placeholder="Escribe el nombre de la IA..." />
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Prompt</label>
                                            <textarea value={form.prompt} onChange={e => setForm(p => ({ ...p, prompt: e.target.value }))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-24 resize-y"
                                                placeholder="Pega aquí el prompt usado..." />
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Notas */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                                <textarea value={form.notas} onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm min-h-16 resize-y"
                                    placeholder="Observaciones adicionales..." />
                            </div>

                            {/* Estado */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                                <div className="flex gap-3">
                                    <button type="button" onClick={() => setForm(p => ({ ...p, estado: 'en_uso' }))}
                                        className={`flex-1 py-2 rounded-lg text-sm font-medium border ${form.estado === 'en_uso' ? 'bg-green-600 text-white border-green-600' : 'border-gray-300 text-gray-600'}`}>
                                        En uso
                                    </button>
                                    <button type="button" onClick={() => setForm(p => ({ ...p, estado: 'sin_uso' }))}
                                        className={`flex-1 py-2 rounded-lg text-sm font-medium border ${form.estado === 'sin_uso' ? 'bg-gray-500 text-white border-gray-500' : 'border-gray-300 text-gray-600'}`}>
                                        Sin uso
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                Cancelar
                            </button>
                            <button onClick={handleSubmit} disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear publicidad')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Publicidad;
